/**
 * ShoppingRoute — /handleliste (Plan 04-03 / UI-SPEC §"<ShoppingRoute />").
 *
 * Route shell composing:
 *   - status uten nett i datopanelet (når navigator.onLine === false)
 *   - <AllergyChip>          (hidden when household has no allergy/dietary data)
 *   - <AddItemForm>          (Enter or the round + button submits)
 *   - mapped <CategorySection>s (in CATEGORY_KEYS order; ukategorisert last)
 *   - "Avkrysset · N" collapsible group (default collapsed via Zustand)
 *   - "Rydd opp" sweep button (visible only when checked count > 0)
 *   - <ConflictToast>        (lives at the route root; reads from store)
 *
 * Data layer:
 *   - `useShoppingList()` for grouped list state + TanStack Query
 *   - `useShoppingWS()` for /ws subscription (id+version dedup)
 *   - `useHouseholdDietary()` shared by the chip and its sheet (#148)
 *
 * Holds `overrideSheetItem` local state — the parent of <ItemRow> in the
 * `onOpenOverride` contract. Plan 05 drops in `<CategoryOverrideSheet>` by
 * reading this state without touching `<ItemRow>`. v1 ships the state slot
 * + a placeholder close handler so the prop is exercised.
 *
 * State branches per UI-SPEC §"Surface Specs":
 *   loading, empty-online, empty-offline, populated-online, populated-offline,
 *   error, idle-returning. Each branch is a render path here.
 *
 * D-19 carry-forward — useIdleTimer is NOT suppressed (per CONTEXT
 * additional_context: tablet idle return is desired UX even from /handleliste).
 *
 * #157 (redesign steg 7d, design/screens/Stell_Handle.dc.html):
 *   1. datopanelet som på Kalender og Middag: «Plan» og avataren, Plan-fanene
 *      i panelet (`drawsOwnHeader` / `ownTabs`), og uten nett «Uten nett ·
 *      N venter» øverst til høyre. Det erstatter offline-pillen over lista;
 *      N er varene som er lagt til uten nett og ikke sendt ennå (`_pending`
 *      i cachen). Avkrysninger og andre endringer i SW-køen ser ikke
 *      klienten, så de telles ikke;
 *   2. allergibanneret (AllergyChip) i amber, aggregert (D4) — navnene står
 *      bak «Detaljer» i arket, ikke i banneret;
 *   3. feltet med rund +-knapp, kategorikort i glass, og raden «Avkrysset · N»
 *      med «Rydd opp». «Avkrysset · N» åpner og lukker fortsatt lista over
 *      avkryssede varer, så de kan krysses av igjen.
 * ⋯ åpner fortsatt kategoriarket; redigering og sletting er ny oppførsel 6.
 * «Fra middag: taco» i skissen har ingen kildekolonne og er utelatt.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ShoppingItem } from '@stell/shared';
import { Button, DatePanel, EmptyState, ErrorState, Icon, LoadingState } from '../../components/primitives';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { MeAvatarLink } from '../../components/ScreenHeader';
import { FeedbackWidget } from '../feedback/FeedbackWidget';
import { SubTabs } from '../dashboard/SubTabsLayout';
import { AddItemForm } from './AddItemForm';
import { AllergyChip } from './AllergyChip';
import { CategoryOverrideSheet } from './CategoryOverrideSheet';
import { CategorySection, SHOPPING_CARD } from './CategorySection';
import { ConflictToast } from './ConflictToast';
import { ItemRow } from './ItemRow';
import { SweepConfirmSheet } from './SweepConfirmSheet';
import { useShoppingList } from './useShoppingList';
import { useShoppingMutations } from './useShoppingMutations';
import { useShoppingStore } from './shoppingStore';
import { useShoppingWS } from './useShoppingWS';

interface MemberBasic {
  id: string;
  short_name: string;
  color_token: ColorSlot;
}

async function fetchMembersBasic(): Promise<MemberBasic[]> {
  const res = await fetch('/api/members/basic', { credentials: 'include' });
  if (!res.ok) return [];
  const json = (await res.json()) as { members?: MemberBasic[] };
  return json.members ?? [];
}

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    function up() {
      setOnline(true);
    }
    function down() {
      setOnline(false);
    }
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function ShoppingRoute() {
  const { t } = useTranslation();
  // Side-effect hooks — open WS on mount, no-op return.
  useShoppingWS();
  // Bring mutations into scope so <AddItemForm>/etc. share a QueryClient session.
  useShoppingMutations();

  const { grouped, isLoading, isError, refetch, pendingIds } = useShoppingList();
  const online = useOnlineStatus();
  const checkedSectionExpanded = useShoppingStore((s) => s.checkedSectionExpanded);
  const toggleCheckedSection = useShoppingStore((s) => s.toggleCheckedSection);

  const [overrideSheetItem, setOverrideSheetItem] = useState<ShoppingItem | null>(null);
  const [sweepOpen, setSweepOpen] = useState(false);

  // Member lookup for attribution chips on each ItemRow.
  const { data: members = [] } = useQuery({
    queryKey: ['members', 'basic'],
    queryFn: fetchMembersBasic,
    staleTime: 5 * 60_000,
  });
  const membersById = useMemo<
    Record<string, { short_name: string; color_token: ColorSlot }>
  >(
    () =>
      Object.fromEntries(
        members.map((m) => [m.id, { short_name: m.short_name, color_token: m.color_token }]),
      ),
    [members],
  );

  const checkedCount = grouped.checked.length;
  const totalRows =
    grouped.checked.length +
    grouped.sections.reduce((acc, s) => acc + s.items.length, 0);
  const isEmpty = totalRows === 0;

  // The override sheet wiring is fulfilled in Plan 05; for Plan 04-03 we
  // expose the state slot + close handler so the prop contract is exercised.
  function handleOpenOverride(item: ShoppingItem) {
    setOverrideSheetItem(item);
  }
  function handleCloseOverride() {
    setOverrideSheetItem(null);
  }

  // Plan 05 wires the CategoryOverrideSheet here. ItemRow's onOpenOverride
  // prop (Plan 04-03) populates `overrideSheetItem`; the sheet reads that
  // state and dispatches updateItem PATCH on chip-pick / Tilbakestill.

  const pendingCount = pendingIds.size;

  const panel = (
    <DatePanel
      title={t('nav.plan')}
      className="md:mx-8 md:mt-4 md:rounded-2xl"
      action={
        <div className="flex min-w-0 items-center gap-2">
          {/* Alltid montert, så skjermlesere leser opp når teksten kommer. */}
          <p role="status" className="m-0 min-w-0">
            {!online ? (
              <span
                data-testid="shopping-offline-banner"
                className="flex items-center gap-1.5 text-right text-caption leading-tight font-extrabold"
              >
                <Icon.WifiOff size={16} className="shrink-0" />
                <span>
                  {pendingCount > 0
                    ? t('shopping.offline.status_pending', { count: pendingCount })
                    : t('shopping.offline.status')}
                </span>
                {/* Den lange forklaringen fra før, for skjermlesere. */}
                <span className="sr-only">{t('shopping.offline.banner')}</span>
              </span>
            ) : null}
          </p>
          {/* #187: tilbakemelding fra skjermen, på panelets flate. */}
          <FeedbackWidget variant="icon" tone="panel" />
          <MeAvatarLink />
        </div>
      }
    >
      <SubTabs group="plan" tone="panel" className="mt-3" />
    </DatePanel>
  );

  return (
    <main
      data-testid="shopping-route"
      aria-label={t('shopping.header')}
      className="flex min-h-dvh flex-col gap-3.5 pb-6"
    >
      {panel}

      <div className="flex flex-col gap-3 px-5 md:px-8 lg:mx-auto lg:w-full lg:max-w-screen-md">
        {/* Plan 06-02 Task 2: ErrorState med «Prøv igjen» når lista ikke kan hentes. */}
        {isError ? (
          <div data-testid="shopping-error">
            <ErrorState
              variant="inline"
              heading={t('shopping.error.load.heading')}
              body={t('shopping.error.load.body')}
              onRetry={() => {
                void refetch();
              }}
            />
          </div>
        ) : null}

        <AllergyChip />

        <AddItemForm />

        {isLoading ? (
          <div data-testid="shopping-loading">
            <LoadingState lines={4} />
          </div>
        ) : isEmpty ? (
          <div data-testid="shopping-empty">
            <EmptyState
              illustration={<Icon.Cart size={28} />}
              heading={t('shopping.empty.title')}
              body={t('shopping.empty.body')}
            />
          </div>
        ) : (
          <>
            {grouped.sections.map((section) => (
              <CategorySection
                key={section.key}
                sectionKey={section.key}
                items={section.items}
                onOpenOverride={handleOpenOverride}
                membersById={membersById}
                pendingIds={pendingIds}
              />
            ))}

            {checkedCount > 0 ? (
              <section aria-labelledby="shopping-checked-title" className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2 pl-1">
                  <button
                    type="button"
                    data-testid="shopping-checked-header"
                    aria-expanded={checkedSectionExpanded}
                    aria-controls="shopping-checked-list"
                    // Navnet er den synlige teksten («Avkrysset · 3»), og
                    // aria-expanded sier om lista er åpen (WCAG 2.5.3).
                    onClick={toggleCheckedSection}
                    className="flex min-h-11 items-center gap-1 rounded-pill pr-2 text-sm font-bold text-ink-2 hover:text-ink"
                  >
                    <span id="shopping-checked-title" className="tnum">
                      {t('shopping.checked.header', { count: checkedCount })}
                    </span>
                    <span
                      data-testid="shopping-checked-toggle"
                      className={[
                        'inline-flex transition-transform',
                        checkedSectionExpanded ? 'rotate-90' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <Icon.ChevR size={18} />
                    </span>
                  </button>
                  <Button
                    variant="secondary"
                    size={44}
                    data-testid="shopping-sweep-button"
                    onClick={() => setSweepOpen(true)}
                  >
                    {t('shopping.sweep.button')}
                  </Button>
                </div>
                {checkedSectionExpanded ? (
                  <ul id="shopping-checked-list" className={`m-0 flex list-none flex-col ${SHOPPING_CARD}`}>
                    {grouped.checked.map((it) => (
                      <li key={it.id}>
                        <ItemRow
                          item={it}
                          onOpenOverride={handleOpenOverride}
                          pending={pendingIds.has(it.id)}
                          addedByMember={membersById[it.added_by]}
                        />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </div>

      <SweepConfirmSheet
        open={sweepOpen}
        count={checkedCount}
        onClose={() => setSweepOpen(false)}
      />
      <CategoryOverrideSheet item={overrideSheetItem} onClose={handleCloseOverride} />
      <ConflictToast />
    </main>
  );
}
